import { FC, useEffect, useState } from 'react';

import classNames from 'classnames';
import { FormattedMessage, useIntl } from 'react-intl';

import styles from './cookie-banner.module.scss';

import {
  OPEN_COOKIE_SETTINGS_EVENT,
  clearTracking,
  getConsent,
  setConsent,
} from 'lib/consent';
import { denyGoogleConsent, isTrackingLoaded, loadTracking } from 'lib/tracking';
import { Button } from 'views/components/UI/Button';
import { Dialog } from 'views/components/UI/Modal';
import { Text } from 'views/components/UI/Text';

const MOBILE_MEDIA_QUERY = '(max-width: 600px)';
const DESCRIPTION_ID = 'cookie-banner-description';
const PRIVACY_POLICY_URL = 'https://www.skiloope.com/privacy-policy';

// A choice is required, so nothing but Accept/Reject dismisses the mobile dialog.
const noop = (): void => undefined;

const CookieBanner: FC = () => {
  const intl = useIntl();
  const [isVisible, setIsVisible] = useState(() => getConsent() === null);
  const [isMobile, setIsMobile] = useState(
    () => window.matchMedia(MOBILE_MEDIA_QUERY).matches,
  );

  useEffect(() => {
    const query = window.matchMedia(MOBILE_MEDIA_QUERY);
    const update = (): void => setIsMobile(query.matches);
    update();
    query.addEventListener('change', update);
    return () => query.removeEventListener('change', update);
  }, []);

  // "Cookie settings" elsewhere in the app asks us to show the choice again.
  useEffect(() => {
    const show = (): void => setIsVisible(true);
    window.addEventListener(OPEN_COOKIE_SETTINGS_EVENT, show);
    return () => window.removeEventListener(OPEN_COOKIE_SETTINGS_EVENT, show);
  }, []);

  const accept = (): void => {
    setConsent('granted');
    loadTracking();
    setIsVisible(false);
  };

  const reject = (): void => {
    // Vendor scripts that already ran can only be unloaded by reloading.
    const needsReload = isTrackingLoaded();
    setConsent('denied');
    denyGoogleConsent();
    clearTracking();
    setIsVisible(false);
    if (needsReload) window.location.reload();
  };

  if (!isVisible) return null;

  const text = (
    <div id={DESCRIPTION_ID} className={styles.text}>
      <Text>
        <FormattedMessage
          id="cookie-banner.text"
          values={{
            privacyPolicy: (
              <a
                href={PRIVACY_POLICY_URL}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={intl.formatMessage({ id: 'cookie-banner.privacy-policy' })}
              >
                <FormattedMessage id="cookie-banner.privacy-policy" />
              </a>
            ),
          }}
        />
      </Text>
    </div>
  );
  const buttons = (
    <div
      className={classNames(styles.actions, { [styles['actions--stacked']]: isMobile })}
    >
      <Button mode="secondary" modBlock={isMobile} onClick={reject}>
        <FormattedMessage id="cookie-banner.reject" />
      </Button>
      <Button mode="secondary" modBlock={isMobile} onClick={accept}>
        <FormattedMessage id="cookie-banner.accept" />
      </Button>
    </div>
  );
  const label = intl.formatMessage({ id: 'cookie-banner.label' });

  return isMobile ? (
    <Dialog
      isOpen
      onClose={noop}
      header={false}
      showCloseButton={false}
      dismissOnEscape={false}
      dismissOnBackdrop={false}
      aria-label={label}
      aria-describedby={DESCRIPTION_ID}
      footer={buttons}
    >
      {text}
    </Dialog>
  ) : (
    <div
      role="region"
      aria-label={label}
      aria-describedby={DESCRIPTION_ID}
      className={styles.banner}
    >
      {text}
      {buttons}
    </div>
  );
};

export { CookieBanner };
